import { useCallback } from "react";
import exceljs from "exceljs";
import {saveAs} from "file-saver";
import { useAuthStore } from "src/components/main/authStore";
import { useUiStore } from "src/components/main/uiStore";
import {
    mimeTypeMap, 
    MyDate, 
} from "src/components/common/functions";
import type { ApprovalRecord } from "./types";

export type ApprovalExportRecord = ApprovalRecord & {
    plannedTime: string;
};

type ExportProps = {
    filteredApprovals: ApprovalExportRecord[];
};

const COLUMN_CONFIG = [
    { name: "Project Name", filterButton: true},
    { name: "Project Category", filterButton: true},
    { name: "Labor Category", filterButton: true},
    { name: "Labor Name", filterButton: true},
    { name: "Department", filterButton: true},
    { name: "Submit Time", filterButton: true},
    { name: "Planned Time", filterButton: true},
];
const MIN_COLUMN_WIDTH = 18;
const DEFAULT_FONT_SIZE = 11;
const FONT_SIZE_MULTIPLIER = 1.2;
const FONT_SIZE_FALLBACK = 1.3;
export default function Export({ filteredApprovals }: ExportProps){
    const user = useAuthStore(state => state.user);
    const setShowLoader = useUiStore((state: { setShowLoader: (showLoader: boolean) => void }) => state.setShowLoader);
    const handleExport = useCallback(async () => {
        try {
            setShowLoader(true);
            const workbook = new exceljs.Workbook();
            workbook.creator = user?.userName || "";
            workbook.created = new Date();
            workbook.calcProperties.fullCalcOnLoad = true;
            const worksheet = workbook.addWorksheet("My Sheet", {views: [{showGridLines: false}]});
            worksheet.state = "visible";
            worksheet.properties.defaultColWidth = 22.57;
            const rows = filteredApprovals.map((approval: ApprovalExportRecord) => [
                approval.projectName,
                approval.projectCategory, 
                approval.laborCategory, 
                approval.userName, 
                approval.department, 
                approval.submitTime, 
                approval.plannedTime
            ]);
            const rowsLength = rows.length;
            worksheet.addTable({
                name: "MyTable",
                ref: "A1",
                totalsRow: true,
                style: {
                    showRowStripes: true,
                },
                columns: COLUMN_CONFIG,
                rows: rows,
            });
            worksheet.columns.forEach((column: Partial<exceljs.Column>) => {
                let maxLength = 0;
                column.eachCell?.({ includeEmpty: true }, cell => {
                    if(Number((cell as exceljs.Cell).row) === 1) return;
                    if(cell.value) {
                        const fontMultiplier = cell.font?.size
                            ? cell.font.size / DEFAULT_FONT_SIZE * FONT_SIZE_MULTIPLIER 
                            : FONT_SIZE_FALLBACK;
                        const columnLength = cell.value.toString().length * fontMultiplier;
                        if(columnLength > maxLength) {
                            maxLength = columnLength;
                        }
                    }
                });
                column.width = Math.max(maxLength, MIN_COLUMN_WIDTH);
            });
            const dataRows = worksheet.getRows(2, rowsLength);
            if(dataRows) {
                dataRows.forEach(row => {
                    row.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
                });
            }
            const buffer = await workbook.xlsx.writeBuffer();
            const fileName = `Approvals export ${(new MyDate()).s()}.xlsx`;
            saveAs(new Blob([buffer], { type: mimeTypeMap.xlsx }), fileName);
        } catch (error) {
            console.error("Export error:", error);
        } finally {
            setShowLoader(false);
        }
    }, [filteredApprovals, user?.userName, setShowLoader]);
    return (
        <button
            type = "button"
            className = "btn btn-outline-primary ms-3" 
            onClick = {handleExport}
        >
            Export
        </button>
    )
}
